import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ChevronLeft, ChevronRight, Lock, Shield } from 'lucide-react';
import { apiClient, getErrorMessage } from '../../packages/api-client';
import {
    getUsersPaginated, getUserEffectivePermissions, getUsers, type User, type EffectivePermission,
} from '../../packages/api-client/pm-api';
import {
    listAccessRequests, listAccessGrants, approveAccessRequest, denyAccessRequest, type AccessRequest,
} from '../../packages/api-client/access-requests-api';
import { Badge, Dialog, PageHeader, StateView, Table, type Column } from '../../packages/ui';
import { usePageTitle } from '../../hooks/usePageTitle';
import './PeopleAccessPage.css';

/**
 * People & Access — the tenant admin's workspace people (handoff §4): users,
 * pending invitations, the one tenant role (admin|user, D4), restriction
 * flags, access requests and the task grants they produced.
 *
 * Superadmins never get here: /people renders the cross-tenant UsersPage for
 * them (workspaceRoutes). Grant expiry and view/edit modes are not built (D7);
 * grant revocation has no endpoint yet (ledger 23e) and is shown as such.
 */

interface Role { id: string; name: string }
interface ProjectOption { id: string; name: string }
interface PendingInvite {
    id: string; email: string; first_name: string; last_name: string;
    expires_at: string; created_at: string;
    is_project_restricted?: boolean; is_task_restricted?: boolean;
}

/** D4: the only tenant roles. Global custom groups are platform-only. */
const TENANT_ROLES = ['admin', 'user'];
const PAGE_LIMIT = 25;

type Load<T> = { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; data: T };

/** Fetches on mount and on reload(); failure is an explicit error state, never an empty list. */
function useLoad<T>(fetch: () => Promise<T>): [Load<T>, () => void, React.Dispatch<React.SetStateAction<Load<T>>>] {
    const [state, setState] = useState<Load<T>>({ status: 'loading' });
    const reload = useCallback(() => {
        setState({ status: 'loading' });
        fetch().then((data) => setState({ status: 'ready', data }))
            .catch((e) => setState({ status: 'error', message: getErrorMessage(e) }));
    }, [fetch]);
    useEffect(reload, [reload]);
    return [state, reload, setState];
}

const fullName = (u: { first_name?: string; last_name?: string; email?: string }) =>
    `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email || '—';
const date = (s?: string) => (s ? new Date(s).toLocaleDateString() : '—');

function RestrictionBadges({ project, task }: { project?: boolean; task?: boolean }) {
    if (!project && !task) return <span className="people-muted">Full workspace</span>;
    return (
        <span className="people-badges">
            {project && <Badge variant="warning"><Lock size={11} aria-hidden="true" /> Project-restricted</Badge>}
            {task && <Badge variant="warning"><Lock size={11} aria-hidden="true" /> Task-restricted</Badge>}
        </span>
    );
}

function Section({ title, children, hint }: { title: string; children: React.ReactNode; hint?: React.ReactNode }) {
    return (
        <section className="people-section" aria-label={title}>
            <h2 className="people-section-title">{title}</h2>
            {hint && <p className="people-muted people-hint">{hint}</p>}
            {children}
        </section>
    );
}

export function PeopleAccessPage() {
    usePageTitle('People & access');

    // ── People ────────────────────────────────────────────────────────────────
    const [page, setPage] = useState(1);
    const fetchUsers = useCallback(() => getUsersPaginated(page, PAGE_LIMIT), [page]);
    const [users, reloadUsers, setUsers] = useLoad(fetchUsers);

    // ── Invitations, requests, grants ─────────────────────────────────────────
    // null = 403: this admin can read people but not invite (users:create).
    const fetchInvites = useCallback(() =>
        apiClient.get<{ data: PendingInvite[] }>('/user-invitations').then((r) => r.data.data ?? [])
            .catch((e) => {
                if ((e as { response?: { status?: number } })?.response?.status === 403) return null;
                throw e;
            }), []);
    const [invites, reloadInvites, setInvites] = useLoad(fetchInvites);
    const [requests, reloadRequests, setRequests] = useLoad(listAccessRequests);
    const [grants, reloadGrants] = useLoad(listAccessGrants);

    // Lookups for the dialogs and for naming requesters. A failure leaves the
    // dropdown without that option (the backend still validates every write).
    const [roles, setRoles] = useState<Role[]>([]);
    const [projects, setProjects] = useState<ProjectOption[]>([]);
    const [names, setNames] = useState<Record<string, string>>({});
    useEffect(() => {
        apiClient.get<{ data: Role[] }>('/roles')
            .then((r) => setRoles((r.data.data ?? []).filter((role) => TENANT_ROLES.includes(role.name))))
            .catch(() => {});
        apiClient.get<{ data: ProjectOption[] }>('/projects', { params: { limit: 200 } })
            .then((r) => setProjects(r.data.data ?? [])).catch(() => {});
        getUsers(200).then((us) => setNames(Object.fromEntries(us.map((u) => [u.id, fullName(u)])))).catch(() => {});
    }, []);
    // Sorted so the select order is stable: admin, user.
    const roleOptions = [...roles].sort((a, b) => a.name.localeCompare(b.name));

    const [inviting, setInviting] = useState(false);
    const [editing, setEditing] = useState<User | null>(null);
    const [busy, setBusy] = useState<string | null>(null);

    async function deactivate(u: User) {
        if (!confirm(`Deactivate ${fullName(u)}? They lose access; their projects and tasks are kept.`)) return;
        setBusy(u.id);
        try {
            // Flip the flag rather than DELETE: a soft-deleted user vanishes from every list.
            const { data } = await apiClient.put<User>(`/users/${u.id}`, { is_active: false });
            setUsers((s) => s.status === 'ready'
                ? { ...s, data: { ...s.data, data: s.data.data.map((x) => x.id === u.id ? { ...x, ...(data ?? {}), is_active: false } : x) } }
                : s);
        } catch (e) { toast.error(getErrorMessage(e)); }
        finally { setBusy(null); }
    }

    async function resendInvite(inv: PendingInvite) {
        setBusy(inv.id);
        try {
            await apiClient.post(`/user-invitations/pending/${inv.id}/resend`);
            toast.success('Invitation email resent');
            reloadInvites();
        } catch (e) { toast.error(getErrorMessage(e)); }
        finally { setBusy(null); }
    }

    async function cancelInvite(inv: PendingInvite) {
        if (!confirm(`Cancel the pending invite to ${inv.email}? The invite link will stop working.`)) return;
        setBusy(inv.id);
        try {
            await apiClient.delete(`/user-invitations/pending/${inv.id}`);
            setInvites((s) => s.status === 'ready' && s.data ? { ...s, data: s.data.filter((i) => i.id !== inv.id) } : s);
            toast.success('Invitation cancelled');
        } catch (e) { toast.error(getErrorMessage(e)); }
        finally { setBusy(null); }
    }

    async function decide(ar: AccessRequest, approve: boolean) {
        setBusy(ar.id);
        try {
            await (approve ? approveAccessRequest(ar.id) : denyAccessRequest(ar.id));
            setRequests((s) => s.status === 'ready' ? { ...s, data: s.data.filter((r) => r.id !== ar.id) } : s);
            toast.success(approve ? 'Access granted' : 'Request denied');
            if (approve) reloadGrants();
        } catch (e) { toast.error(getErrorMessage(e)); }
        finally { setBusy(null); }
    }

    const requester = (id: string) => names[id] ?? `${id.slice(0, 8)}…`;
    const resourceLink = (ar: AccessRequest) =>
        <Link to={`/${ar.resource_type}s/${ar.resource_id}`}>Open {ar.resource_type}</Link>;

    const userCols: Column<User & Record<string, unknown>>[] = [
        { key: 'name', header: 'Name', rowHeader: true, render: (u) => fullName(u) },
        { key: 'email', header: 'Email' },
        {
            key: 'role', header: 'Role', render: (u) => (u.roles ?? []).length
                ? (u.roles ?? []).join(', ') : <span className="people-muted">No role</span>,
        },
        { key: 'access', header: 'Access', render: (u) => <RestrictionBadges project={u.is_project_restricted} task={u.is_task_restricted} /> },
        { key: 'status', header: 'Status', render: (u) => <Badge variant={u.is_active ? 'success' : 'danger'}>{u.is_active ? 'Active' : 'Inactive'}</Badge> },
        {
            key: 'actions', header: 'Actions', render: (u) => (
                <span className="people-actions">
                    <button type="button" className="btn btn-ghost btn-sm" aria-label={`Edit ${fullName(u)}`} onClick={() => setEditing(u)}>Edit</button>
                    <button type="button" className="btn btn-ghost btn-sm people-danger" aria-label={`Deactivate ${fullName(u)}`}
                        disabled={!u.is_active || busy === u.id} onClick={() => deactivate(u)}>
                        {u.is_active ? 'Deactivate' : 'Inactive'}
                    </button>
                </span>
            ),
        },
    ];

    const inviteCols: Column<PendingInvite & Record<string, unknown>>[] = [
        { key: 'email', header: 'Email', rowHeader: true },
        { key: 'name', header: 'Name', render: (i) => `${i.first_name} ${i.last_name}`.trim() || '—' },
        { key: 'access', header: 'Access', render: (i) => <RestrictionBadges project={i.is_project_restricted} task={i.is_task_restricted} /> },
        {
            key: 'status', header: 'Status', render: (i) => new Date(i.expires_at).getTime() < Date.now()
                ? <Badge variant="danger">Expired</Badge>
                : <span className="people-muted">Sent {date(i.created_at)} · expires {date(i.expires_at)}</span>,
        },
        {
            key: 'actions', header: 'Actions', render: (i) => (
                <span className="people-actions">
                    <button type="button" className="btn btn-ghost btn-sm" disabled={busy === i.id} onClick={() => resendInvite(i)}>Resend</button>
                    <button type="button" className="btn btn-ghost btn-sm people-danger" disabled={busy === i.id} onClick={() => cancelInvite(i)}>Cancel</button>
                </span>
            ),
        },
    ];

    const requestCols: Column<AccessRequest & Record<string, unknown>>[] = [
        { key: 'who', header: 'Person', rowHeader: true, render: (r) => requester(r.user_id) },
        { key: 'what', header: 'Requested', render: (r) => <>{r.resource_type} · {resourceLink(r)}</> },
        { key: 'note', header: 'Note', render: (r) => r.note ?? <span className="people-muted">—</span> },
        { key: 'when', header: 'Requested on', render: (r) => date(r.created_at) },
        {
            key: 'actions', header: 'Actions', render: (r) => (
                <span className="people-actions">
                    <button type="button" className="btn btn-secondary btn-sm" disabled={busy === r.id} onClick={() => decide(r, false)}>Deny</button>
                    <button type="button" className="btn btn-primary btn-sm" disabled={busy === r.id} onClick={() => decide(r, true)}>Approve</button>
                </span>
            ),
        },
    ];

    const grantCols: Column<AccessRequest & Record<string, unknown>>[] = [
        { key: 'who', header: 'Person', rowHeader: true, render: (r) => requester(r.user_id) },
        { key: 'what', header: 'Resource', render: (r) => resourceLink(r) },
        { key: 'granted', header: 'Granted on', render: (r) => date(r.decided_at) },
        { key: 'by', header: 'Granted by', render: (r) => (r.decided_by ? requester(r.decided_by) : '—') },
    ];

    const total = users.status === 'ready' ? users.data.meta?.total ?? users.data.data.length : 0;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));
    const taskGrants = grants.status === 'ready' ? grants.data.filter((g) => g.resource_type === 'task') : [];

    return (
        <div className="people-page">
            <PageHeader
                title="People & access"
                subtitle="Who is in this workspace, their role, and what they can see."
                actions={<button type="button" className="btn btn-primary btn-md" onClick={() => setInviting(true)}>Invite person</button>}
            />

            <Section title="People">
                {users.status === 'loading' && <StateView state="loading" label="Loading people" />}
                {users.status === 'error' && <StateView state="error" title="People couldn't be loaded" message={users.message} onRetry={reloadUsers} />}
                {users.status === 'ready' && (users.data.data.length === 0
                    ? <StateView state="empty" title="No people yet" message="Invite someone to get started." />
                    : <Table caption="People" hideCaption columns={userCols}
                        data={users.data.data as (User & Record<string, unknown>)[]} rowKey={(u) => u.id} />)}
                {users.status === 'ready' && totalPages > 1 && (
                    <div className="people-pager">
                        <button type="button" className="btn btn-ghost btn-sm" aria-label="Previous page" disabled={page === 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft size={16} aria-hidden="true" /></button>
                        <span className="people-muted">Page {page} of {totalPages} · {total} total</span>
                        <button type="button" className="btn btn-ghost btn-sm" aria-label="Next page" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}><ChevronRight size={16} aria-hidden="true" /></button>
                    </div>
                )}
            </Section>

            <Section title="Pending invitations">
                {invites.status === 'loading' && <StateView state="loading" label="Loading invitations" />}
                {invites.status === 'error' && <StateView state="error" title="Invitations couldn't be loaded" message={invites.message} onRetry={reloadInvites} />}
                {invites.status === 'ready' && invites.data === null && (
                    <p className="people-muted">Invitations are managed by admins who can invite.</p>
                )}
                {invites.status === 'ready' && invites.data && (invites.data.length === 0
                    ? <StateView state="empty" title="No pending invitations" />
                    : <Table caption="Pending invitations" hideCaption columns={inviteCols}
                        data={invites.data as (PendingInvite & Record<string, unknown>)[]} rowKey={(i) => i.id} />)}
            </Section>

            <Section title="Access requests" hint="Requests you can decide: you own the task or its project, or you are a project owner or admin.">
                {requests.status === 'loading' && <StateView state="loading" label="Loading access requests" />}
                {requests.status === 'error' && <StateView state="error" title="Access requests couldn't be loaded" message={requests.message} onRetry={reloadRequests} />}
                {requests.status === 'ready' && (requests.data.length === 0
                    ? <StateView state="empty" title="No pending access requests" />
                    : <Table caption="Access requests" hideCaption columns={requestCols}
                        data={requests.data as (AccessRequest & Record<string, unknown>)[]} rowKey={(r) => r.id} />)}
            </Section>

            <Section title="Task grants" hint={<>
                An approved task request lets that person open one task outside their restriction.
                Revoking a grant isn't available yet. Approved project requests are project memberships;
                remove them from the project's Members section.
            </>}>
                {grants.status === 'loading' && <StateView state="loading" label="Loading task grants" />}
                {grants.status === 'error' && <StateView state="error" title="Task grants couldn't be loaded" message={grants.message} onRetry={reloadGrants} />}
                {grants.status === 'ready' && (taskGrants.length === 0
                    ? <StateView state="empty" title="No task grants" />
                    : <Table caption="Task grants" hideCaption columns={grantCols}
                        data={taskGrants as (AccessRequest & Record<string, unknown>)[]} rowKey={(r) => r.id} />)}
            </Section>

            {inviting && (
                <InviteDialog onClose={() => setInviting(false)} roles={roleOptions} projects={projects}
                    onSent={() => { setInviting(false); reloadInvites(); }} />
            )}
            {editing && (
                <EditDialog user={editing} roles={roleOptions} onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); reloadUsers(); }} />
            )}
        </div>
    );
}

// ─── Invite ──────────────────────────────────────────────────────────────────

function RoleSelect({ id, roles, value, onChange, legacy }: {
    id: string; roles: Role[]; value: string; onChange: (v: string) => void;
    /** The user's current role when it isn't admin|user: shown, never offered. */
    legacy?: Role;
}) {
    return (
        <div className="form-group">
            <label className="form-label" htmlFor={id}>Workspace role</label>
            <select id={id} className="form-input" value={value} onChange={(e) => onChange(e.target.value)}>
                <option value="">No role assigned</option>
                {legacy && <option value={legacy.id} disabled>{legacy.name} (legacy, not assignable)</option>}
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <p className="people-muted people-hint">admin manages people, roles and billing; user does project work.</p>
        </div>
    );
}

function RestrictionFields({ project, task, onProject, onTask }: {
    project: boolean; task: boolean; onProject: (v: boolean) => void; onTask: (v: boolean) => void;
}) {
    return (
        <fieldset className="people-fieldset">
            <legend className="form-label">Access scope</legend>
            <label className="people-check">
                <input type="checkbox" checked={project} onChange={(e) => onProject(e.target.checked)} />
                Project-restricted — only sees projects they belong to
            </label>
            <label className="people-check">
                <input type="checkbox" checked={task} onChange={(e) => onTask(e.target.checked)} />
                Task-restricted — only sees tasks assigned to or created by them
            </label>
        </fieldset>
    );
}

function InviteDialog({ onClose, onSent, roles, projects }: {
    onClose: () => void; onSent: () => void; roles: Role[]; projects: ProjectOption[];
}) {
    const [f, setF] = useState({ email: '', first_name: '', last_name: '', role_id: '', project_id: '', project_role: 'member', p: false, t: false });
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setSending(true); setError(null);
        try {
            // The invitee sets their own password; no user row exists until they accept.
            await apiClient.post('/user-invitations', {
                email: f.email, first_name: f.first_name, last_name: f.last_name,
                ...(f.role_id ? { role_id: f.role_id } : {}),
                is_project_restricted: f.p, is_task_restricted: f.t,
                ...(f.project_id ? { project_id: f.project_id, project_role: f.project_role } : {}),
            });
            toast.success(`Invitation sent to ${f.email}`);
            onSent();
        } catch (err) { setError(getErrorMessage(err)); }
        finally { setSending(false); }
    }

    return (
        <Dialog open onClose={onClose} title="Invite person" closeOnBackdrop={false}>
            <form onSubmit={submit} className="people-form">
                {error && <p role="alert" className="people-error">{error}</p>}
                <p className="people-muted">We'll email an invite link. They set their own password when they accept.</p>
                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label" htmlFor="inv-first">First name</label>
                        <input id="inv-first" className="form-input" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} />
                    </div>
                    <div className="form-group">
                        <label className="form-label" htmlFor="inv-last">Last name</label>
                        <input id="inv-last" className="form-input" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} />
                    </div>
                </div>
                <div className="form-group">
                    <label className="form-label" htmlFor="inv-email">Email</label>
                    <input id="inv-email" className="form-input" type="email" required autoFocus value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
                </div>
                <RoleSelect id="inv-role" roles={roles} value={f.role_id} onChange={(v) => setF({ ...f, role_id: v })} />
                <RestrictionFields project={f.p} task={f.t} onProject={(v) => setF({ ...f, p: v })} onTask={(v) => setF({ ...f, t: v })} />
                {projects.length > 0 && (
                    <div className="form-row">
                        <div className="form-group">
                            <label className="form-label" htmlFor="inv-project">Add to project (optional)</label>
                            <select id="inv-project" className="form-input" value={f.project_id} onChange={(e) => setF({ ...f, project_id: e.target.value })}>
                                <option value="">No project</option>
                                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>
                        {f.project_id && (
                            <div className="form-group">
                                <label className="form-label" htmlFor="inv-project-role">Project role</label>
                                <select id="inv-project-role" className="form-input" value={f.project_role} onChange={(e) => setF({ ...f, project_role: e.target.value })}>
                                    <option value="member">Member</option>
                                    <option value="viewer">Viewer</option>
                                    <option value="admin">Admin</option>
                                    <option value="owner">Owner</option>
                                </select>
                            </div>
                        )}
                    </div>
                )}
                <div className="people-form-footer">
                    <button type="button" className="btn btn-secondary btn-md" onClick={onClose}>Cancel</button>
                    <button type="submit" className="btn btn-primary btn-md" disabled={sending}>{sending ? 'Sending…' : 'Send invitation'}</button>
                </div>
            </form>
        </Dialog>
    );
}

// ─── Edit + effective access ─────────────────────────────────────────────────

function EditDialog({ user, roles, onClose, onSaved }: { user: User; roles: Role[]; onClose: () => void; onSaved: () => void }) {
    const [f, setF] = useState({
        email: user.email, first_name: user.first_name ?? '', last_name: user.last_name ?? '', is_active: user.is_active,
        p: !!user.is_project_restricted, t: !!user.is_task_restricted,
    });
    const [roleId, setRoleId] = useState('');
    const [origRoleIds, setOrigRoleIds] = useState<string[]>([]);
    const [legacyRole, setLegacyRole] = useState<Role | undefined>();
    const [perms, setPerms] = useState<Load<EffectivePermission[]>>({ status: 'loading' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        apiClient.get<{ data: Role[] }>(`/users/${user.id}/roles`).then((r) => {
            const ids = (r.data.data ?? []).map((x) => x.id);
            setOrigRoleIds(ids);
            const current = (r.data.data ?? [])[0];
            if (current && !TENANT_ROLES.includes(current.name)) setLegacyRole(current);
            if (ids[0]) setRoleId(ids[0]);
        }).catch(() => {});
        getUserEffectivePermissions(user.id)
            .then((data) => setPerms({ status: 'ready', data }))
            .catch((e) => setPerms({ status: 'error', message: getErrorMessage(e) }));
    }, [user.id]);

    const emailChanged = f.email.trim() !== user.email.trim();

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setSaving(true); setError(null);
        try {
            await apiClient.put<User>(`/users/${user.id}`, {
                first_name: f.first_name, last_name: f.last_name, is_active: f.is_active,
                is_project_restricted: f.p, is_task_restricted: f.t,
                ...(emailChanged ? { email: f.email.trim() } : {}),
            });
            if (emailChanged) toast.success("Email updated. The user's sessions were signed out and both addresses notified.");
            // One POST: the server replaces the user's single role atomically.
            if (roleId && !origRoleIds.includes(roleId)) await apiClient.post(`/users/${user.id}/roles/${roleId}`);
            onSaved();
        } catch (err) { setError(getErrorMessage(err)); }
        finally { setSaving(false); }
    }

    return (
        <Dialog open onClose={onClose} title={`Edit ${fullName(user)}`} closeOnBackdrop={false}>
            <form onSubmit={submit} className="people-form">
                {error && <p role="alert" className="people-error">{error}</p>}
                <div className="form-group">
                    <label className="form-label" htmlFor="ed-email">Email</label>
                    <input id="ed-email" className="form-input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
                    {emailChanged && <p className="people-warn">Saving signs this user out everywhere and notifies both addresses.</p>}
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label" htmlFor="ed-first">First name</label>
                        <input id="ed-first" className="form-input" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} />
                    </div>
                    <div className="form-group">
                        <label className="form-label" htmlFor="ed-last">Last name</label>
                        <input id="ed-last" className="form-input" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} />
                    </div>
                </div>
                <div className="form-group">
                    <label className="form-label" htmlFor="ed-status">Status</label>
                    <select id="ed-status" className="form-input" value={f.is_active ? 'active' : 'inactive'} onChange={(e) => setF({ ...f, is_active: e.target.value === 'active' })}>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </div>
                <RoleSelect id="ed-role" roles={roles} value={roleId} onChange={setRoleId} legacy={legacyRole} />
                <RestrictionFields project={f.p} task={f.t} onProject={(v) => setF({ ...f, p: v })} onTask={(v) => setF({ ...f, t: v })} />

                <div className="people-effective">
                    <h3 className="people-effective-title"><Shield size={14} aria-hidden="true" /> Effective access (as saved)</h3>
                    <ul className="people-effective-list">
                        <li>{user.is_project_restricted
                            ? 'Sees only projects they are a member of. Membership is managed in each project\'s Members section.'
                            : 'Sees every project in the workspace.'}</li>
                        <li>{user.is_task_restricted
                            ? 'Sees only tasks assigned to or created by them, plus tasks they were granted.'
                            : 'Sees every task in the projects they can see.'}</li>
                    </ul>
                    {perms.status === 'loading' && <StateView state="loading" label="Loading permissions" lines={1} />}
                    {perms.status === 'error' && <p className="people-error">Permissions couldn't be loaded: {perms.message}</p>}
                    {perms.status === 'ready' && (perms.data.length === 0
                        ? <p className="people-muted">No permissions — assign a role to grant access.</p>
                        : <ul className="people-perms" aria-label="Role permissions">
                            {perms.data.map((p) => <li key={`${p.role}:${p.resource}:${p.action}`}><code>{p.resource}:{p.action}</code></li>)}
                        </ul>)}
                </div>

                <div className="people-form-footer">
                    <button type="button" className="btn btn-secondary btn-md" onClick={onClose}>Cancel</button>
                    <button type="submit" className="btn btn-primary btn-md" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
                </div>
            </form>
        </Dialog>
    );
}
